/**
 * Pre-booking queue — calls the AI line took, waiting to become real bookings.
 *
 * The bot writes down what it heard and stops. Nothing here is a booking yet, and nothing
 * here has been validated: the names and regos are raw speech-recognition output, which is
 * exactly why a human reads them before anything is committed.
 *
 * Two actions per row:
 *   Book    — opens the normal booking form pre-filled with what the caller said, so the
 *             real Customer/Vehicle/Booking are created through the same validated path as
 *             any staff booking.
 *   Dismiss — junk transcription, wrong number, or the customer was handled another way.
 *
 * The caller's number is the most important field on the row: the promise made on the phone
 * was "the workshop will call you back on this number".
 */

import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import VoiceCorrectionNote from './VoiceCorrectionNote';
import { Phone, X, ChevronLeft, ChevronRight, RefreshCw, CalendarPlus, Trash2, Copy, Check } from 'lucide-react';
import { fetchPreBookings, dismissPreBooking } from '../store/slices/phoneBookingSlice';

const formatReceived = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
        day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
    });
};

const formatDay = (iso) => {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
};

/**
 * Copies to the clipboard, falling back when the async API is unavailable.
 *
 * navigator.clipboard requires a secure context and is refused outright by some in-app
 * browsers. Silently copying nothing is worse than not offering the button at all, so the
 * textarea + execCommand path stays.
 */
const copyText = (value, onDone) => {
    if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(value).then(onDone).catch(() => legacyCopy(value, onDone));
        return;
    }
    legacyCopy(value, onDone);
};

const legacyCopy = (value, onDone) => {
    try {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        onDone();
    } catch {
        /* nothing sensible left to do — leave the icon unchanged rather than lie */
    }
};

/**
 * A value with a one-tap copy button.
 *
 * Staff ring the customer from a mobile and then retype the number, name and rego into the
 * booking form. Hand-keying a mobile is exactly where a call-back reaches the wrong person,
 * so the three transcribed fields are copyable rather than merely readable.
 */
const CopyableField = ({ value, label, children, className = '' }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e) => {
        e.stopPropagation();          // a copy tap must never open the booking form
        if (!value) return;
        copyText(value, () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        });
    };

    return (
        <span className={`inline-flex items-center gap-1 min-w-0 ${className}`}>
            {children}
            <button
                type="button"
                onClick={handleCopy}
                aria-label={`Copy ${label}`}
                title={`Copy ${label}`}
                className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
                {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
            </button>
        </span>
    );
};

const BotBookingsPanel = ({ open, onClose, onBook }) => {
    const dispatch = useDispatch();
    const {
        preBookings, page, size, totalItems, totalPages, loading, error,
    } = useSelector((state) => state.preBookings);

    const load = useCallback(
        (nextPage = 0) => dispatch(fetchPreBookings({ page: nextPage, size })),
        [dispatch, size]
    );

    if (!open) return null;

    const canPrev = page > 0;
    const canNext = page + 1 < totalPages;

    return (
        <aside
            className="w-80 shrink-0 border-l border-gray-200 bg-gray-50 flex flex-col max-h-[70vh]"
            aria-label="Phone calls waiting to be booked"
        >
            <header className="border-b border-gray-200 bg-white px-3 py-2">
                <div className="flex items-start justify-between">
                    <div>
                        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                            <Phone size={14} className="text-emerald-600" />
                            Calls to book
                        </h3>
                        <p className="text-xs text-gray-500">
                            {totalItems === 0
                                ? 'Nothing waiting'
                                : `${totalItems} ${totalItems === 1 ? 'customer' : 'customers'} to call back`}
                        </p>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => load(page)}
                            disabled={loading}
                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
                            aria-label="Refresh"
                        >
                            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            aria-label="Close"
                        >
                            <X size={15} />
                        </button>
                    </div>
                </div>
            </header>

            <div className="flex-1 overflow-y-auto p-2">
                {error && (
                    <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                        {error}
                    </div>
                )}

                {loading && preBookings.length === 0 && (
                    <p className="py-8 text-center text-xs text-gray-500">Loading…</p>
                )}

                {!loading && preBookings.length === 0 && !error && (
                    <p className="px-2 py-8 text-center text-xs text-gray-500">
                        No calls waiting. New ones appear here as they come in.
                    </p>
                )}

                <ul className="space-y-2">
                    {preBookings.map((p) => (
                        <li key={p.uuid} className="rounded-lg border border-amber-200 bg-white p-2.5">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-[11px] text-gray-500">
                                    {formatReceived(p.receivedAt)}
                                </span>
                                <span className="font-mono text-[10px] text-gray-400">
                                    {p.referenceCode}
                                </span>
                            </div>

                            <CopyableField value={p.customerName} label="name" className="mt-1 max-w-full">
                                <span className="truncate text-sm font-medium text-gray-900">
                                    {p.customerName || 'Name not caught'}
                                </span>
                            </CopyableField>

                            {/* The promise made on the phone was a call back to this number. */}
                            {p.callerNumber && (
                                <CopyableField value={p.callerNumber} label="mobile" className="block">
                                    <a
                                        href={`tel:${p.callerNumber}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-sm text-blue-600 hover:underline"
                                    >
                                        {p.callerNumber}
                                    </a>
                                </CopyableField>
                            )}

                            <div className="mt-1 flex items-center gap-1 text-xs text-gray-600">
                                {p.vehicleRego && (
                                    <CopyableField value={p.vehicleRego} label="rego">
                                        <span className="font-mono">{p.vehicleRego}</span>
                                    </CopyableField>
                                )}
                                <span className="truncate">
                                    {p.vehicleRego ? '· ' : ''}{p.spokenService || 'No service given'}
                                </span>
                            </div>

                            {p.requestedDate && (
                                <div className="text-xs text-gray-500">
                                    Wanted {formatDay(p.requestedDate)}
                                </div>
                            )}

                            {/* THE CALLER'S OWN WORDS — the most useful thing on this card
                                when ringing back. The resolved service above says "brake
                                check"; this says why they rang. Quoted so it reads as
                                theirs, and never truncated for the same reason. */}
                            {(p.callerServiceWords || p.lastUtterance) && (
                                <div className="mt-1.5 rounded-md bg-gray-50 px-2 py-1.5">
                                    <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                                        Caller said
                                    </p>
                                    <p className="text-xs text-gray-700">
                                        “{p.callerServiceWords || p.lastUtterance}”
                                    </p>
                                </div>
                            )}

                            <VoiceCorrectionNote item={p} />

                            <p className="mt-1.5 text-[11px] italic text-gray-400">
                                Heard by the bot — check before booking.
                            </p>

                            <div className="mt-2 flex gap-1.5">
                                <button
                                    type="button"
                                    onClick={() => onBook?.(p)}
                                    className="flex flex-1 items-center justify-center gap-1 rounded-md bg-emerald-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
                                >
                                    <CalendarPlus size={12} />
                                    Book
                                </button>
                                <button
                                    type="button"
                                    onClick={() => dispatch(dismissPreBooking(p.uuid))}
                                    className="flex items-center justify-center gap-1 rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
                                    title="Not a real booking — remove from this list"
                                >
                                    <Trash2 size={12} />
                                    Dismiss
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {totalPages > 1 && (
                <footer className="flex items-center justify-between border-t border-gray-200 bg-white px-3 py-2">
                    <button
                        type="button"
                        onClick={() => load(page - 1)}
                        disabled={!canPrev || loading}
                        className="flex items-center gap-0.5 rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                    >
                        <ChevronLeft size={13} /> Prev
                    </button>
                    <span className="text-xs text-gray-500">
                        Page {page + 1} of {totalPages}
                    </span>
                    <button
                        type="button"
                        onClick={() => load(page + 1)}
                        disabled={!canNext || loading}
                        className="flex items-center gap-0.5 rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                    >
                        Next <ChevronRight size={13} />
                    </button>
                </footer>
            )}
        </aside>
    );
};

export default BotBookingsPanel;
